import { useState, useEffect } from 'react';
import {
    TrendingUp, TrendingDown, Minus, Layers, Loader2, Repeat,
    Package, ClipboardList, Coins, Truck,
} from 'lucide-react';
import { fmtCena, fmtDate } from '../receptura/receptUtils';

/**
 * Stálá karta vpravo u materiálu — protějšek kalkulační karty receptury
 * a drží její rytmus: kruh s hlavním číslem, graf, rozpad 2×2, info dlaždice.
 *
 * Kruh tu neukazuje složení (materiál žádné nemá), ale kde se dnešní cena
 * pohybuje mezi svým minimem a maximem. Kdo nakupuje, tohle chce vědět dřív
 * než absolutní částku: „jsme na stropě, nebo na dně?"
 */
export default function MaterialStatistika({
    typ, detail, ceny = [], isDark, mutedColor, secondaryColor, cardStyle, sekceBarva = '#F59E0B',
    onPouziti,
}) {
    const [pouziti, setPouziti] = useState(null);
    const [nacitam, setNacitam] = useState(false);

    const trackColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const tileBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const tileBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const id = detail?.id;

    useEffect(() => {
        if (!id) { setPouziti(null); return; }
        let zije = true;
        setNacitam(true);
        fetch(`/api/recepty/pouziti/${typ}/${id}`, { headers: { Accept: 'application/json' } })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (zije) setPouziti(d); })
            .catch(() => {})
            .finally(() => { if (zije) setNacitam(false); });
        return () => { zije = false; };
    }, [typ, id]);

    if (!detail) return null;

    const historie = [...ceny]
        .filter(c => c.cena_za_jednotku != null)
        .sort((a, b) => String(a.platnost_od).localeCompare(String(b.platnost_od)));

    const aktualni = historie.find(c => c.je_primarni) ?? historie[historie.length - 1];
    const prvni = historie[0];
    const hodnoty = historie.map(c => Number(c.cena_za_jednotku) || 0);
    const min = hodnoty.length ? Math.min(...hodnoty) : 0;
    const max = hodnoty.length ? Math.max(...hodnoty) : 0;
    const cena = aktualni ? Number(aktualni.cena_za_jednotku) : null;

    const zmenaPct = historie.length > 1 && prvni?.cena_za_jednotku > 0
        ? ((cena - prvni.cena_za_jednotku) / prvni.cena_za_jednotku) * 100
        : null;

    // Kruh: kde dnešní cena leží mezi minimem a maximem historie.
    // Bez historie zůstane prázdný — není z čeho pozici určit.
    const pozice = max > min ? (cena - min) / (max - min) : null;
    const size = 190, sw = 26, R = (size - sw) / 2, C = 2 * Math.PI * R, cx = size / 2;
    const vyplnena = pozice == null ? 0 : Math.max(0.02, pozice) * C;

    const jed = detail.jednotka?.zkratka || '';
    const Sipka = zmenaPct == null ? Minus : zmenaPct > 0 ? TrendingUp : TrendingDown;
    const barvaZmeny = zmenaPct == null ? mutedColor : zmenaPct > 0 ? '#EF4444' : '#10B981';
    const barvaKruhu = pozice == null ? sekceBarva : pozice > 0.75 ? '#EF4444' : pozice < 0.25 ? '#10B981' : sekceBarva;

    const receptur = pouziti ? new Set(pouziti.pouziti.map(p => p.recept.id)).size : 0;
    const predpisu = pouziti ? new Set((pouziti.predpisy || []).map(p => p.predpis.id)).size : 0;
    const radku = (pouziti?.pouziti?.length || 0) + (pouziti?.predpisy?.length || 0);

    const infoTile = (Icon, value, label, zvyraznit = false) => (
        <div className="rounded-lg py-2.5 flex flex-col items-center gap-1" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
            <Icon size={15} style={{ color: zvyraznit ? sekceBarva : mutedColor }} />
            <div className="text-sm font-bold leading-none" style={{ color: 'var(--text-primary)' }}>{value}</div>
            <div className="text-[9px] text-center" style={{ color: mutedColor }}>{label}</div>
        </div>
    );

    return (
        <div className="rounded-xl flex flex-col" style={{ ...cardStyle, flexShrink: 0 }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: secondaryColor }}>Cena a použití</span>
                {nacitam
                    ? <Loader2 size={12} className="animate-spin" style={{ color: mutedColor }} />
                    : historie.length > 1 && <span className="text-[10px]" style={{ color: mutedColor }}>{historie.length} ceníků</span>}
            </div>

            <div className="p-4">
                {/* Kruh — kde dnešní cena leží ve svém rozpětí */}
                <div className="relative" style={{ width: size, height: size, margin: '0 auto' }}>
                    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx={cx} cy={cx} r={R} fill="none" stroke={trackColor} strokeWidth={sw} />
                        {pozice != null && (
                            <circle cx={cx} cy={cx} r={R} fill="none" stroke={barvaKruhu} strokeWidth={sw} strokeLinecap="butt"
                                strokeDasharray={`${vyplnena} ${C - vyplnena}`}
                                style={{ transition: 'stroke-dasharray 0.4s' }} />
                        )}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        {cena != null ? (
                            <>
                                <div className="text-[28px] font-bold leading-none" style={{ color: 'var(--text-primary)' }}>{cena.toFixed(2)}</div>
                                <div className="text-[11px] mt-1" style={{ color: mutedColor }}>Kč{jed ? ` / ${jed}` : ''}</div>
                                {zmenaPct != null && (
                                    <div className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                        style={{ background: `${barvaZmeny}1f`, color: barvaZmeny }}>
                                        <Sipka size={10} />{zmenaPct > 0 ? '+' : ''}{zmenaPct.toFixed(1)} %
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <Coins size={24} style={{ color: mutedColor }} />
                                <div className="text-xs mt-2 font-semibold" style={{ color: '#EF4444' }}>Bez ceny</div>
                                <div className="text-[10px] mt-0.5 text-center px-6" style={{ color: mutedColor }}>do kalkulace vstupuje nulou</div>
                            </>
                        )}
                    </div>
                </div>

                {pozice != null && (
                    <p className="text-[10px] text-center mt-2" style={{ color: mutedColor }}>
                        {pozice > 0.75 ? 'Cena je u svého maxima' : pozice < 0.25 ? 'Cena je u svého minima' : 'Cena je uprostřed svého rozpětí'}
                    </p>
                )}

                {/* Vývoj ceny */}
                <div className="mt-4 rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: mutedColor }}>Vývoj ceny</div>

                    {historie.length <= 1 ? (
                        <p className="text-[11px]" style={{ color: mutedColor }}>
                            {historie.length === 1
                                ? `Jediná cena, platná od ${fmtDate(historie[0].platnost_od)} — historie zatím není z čeho kreslit.`
                                : 'Zatím žádný ceník.'}
                        </p>
                    ) : (
                        <>
                            {/* Sloupce, ne křivka — ceník je skokový, křivka by lhala o spojitosti */}
                            <div className="flex items-end gap-1" style={{ height: 56 }}>
                                {historie.map((c, i) => {
                                    const v = Number(c.cena_za_jednotku) || 0;
                                    const posledni = i === historie.length - 1;
                                    return (
                                        <div key={c.id ?? i} className="flex-1 rounded-t"
                                            title={`${fmtDate(c.platnost_od)}: ${fmtCena(v)}`}
                                            style={{
                                                height: `${Math.max(8, (v / (max || 1)) * 100)}%`,
                                                background: posledni ? sekceBarva : (isDark ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.16)'),
                                                minWidth: 6, transition: 'height 0.3s',
                                            }} />
                                    );
                                })}
                            </div>
                            <div className="flex items-center justify-between mt-1.5 text-[10px]" style={{ color: mutedColor }}>
                                <span>{fmtDate(prvni.platnost_od)}</span>
                                <span>{fmtDate(aktualni.platnost_od)}</span>
                            </div>
                        </>
                    )}
                </div>

                {/* Rozpad ceny 2×2 */}
                {historie.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                            <div className="text-[11px] mb-1" style={{ color: secondaryColor }}>Nejnižší</div>
                            <div className="text-lg font-bold leading-none" style={{ color: '#10B981' }}>
                                {min.toFixed(2)} <span className="text-[11px] font-normal" style={{ color: mutedColor }}>Kč</span>
                            </div>
                        </div>
                        <div className="rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                            <div className="text-[11px] mb-1" style={{ color: secondaryColor }}>Nejvyšší</div>
                            <div className="text-lg font-bold leading-none" style={{ color: '#EF4444' }}>
                                {max.toFixed(2)} <span className="text-[11px] font-normal" style={{ color: mutedColor }}>Kč</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Info dlaždice */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                    {infoTile(Truck, aktualni?.dodavatel?.nazev
                        ? (aktualni.dodavatel.nazev.length > 12 ? aktualni.dodavatel.nazev.slice(0, 11) + '…' : aktualni.dodavatel.nazev)
                        : '—', 'dodavatel')}
                    {infoTile(Package, receptur, receptur === 1 ? 'receptura' : 'receptur', receptur > 0)}
                    {infoTile(ClipboardList, predpisu, predpisu === 1 ? 'předpis' : 'předpisů', predpisu > 0)}
                    {infoTile(Layers, radku, 'řádků celkem', radku > 0)}
                </div>

                <button onClick={onPouziti} disabled={radku === 0}
                    className="flex items-center justify-center gap-1.5 w-full mt-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{
                        background: radku === 0 ? 'none' : sekceBarva,
                        color: radku === 0 ? mutedColor : '#fff',
                        border: radku === 0 ? `1px dashed ${tileBorder}` : 'none',
                        cursor: radku === 0 ? 'default' : 'pointer',
                    }}>
                    <Repeat size={13} /> {radku === 0 ? 'Zatím nikde nefiguruje' : 'Zobrazit a nahradit'}
                </button>
            </div>
        </div>
    );
}
